import { Dialog, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { Dayjs } from 'dayjs'
import { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'

import FlatLayout from 'components/layouts/flatLayout'
import ModalLayout from 'components/layouts/modalLayout'
import DatePickerForm from 'components/parts/forms/datePickerForm'
import { helper } from 'helper'
import { securityFieldApi } from 'hooks/api/securityField'
import { shiftApi } from 'hooks/api/shift'
import { shiftQuestionnaireApi } from 'hooks/api/shiftQuestionnaire'
import EditComponent from 'pages/shift/show/components/edit'
import NotesModal from 'pages/shift/show/components/notesModal'
import ResultComponent from 'pages/shift/show/components/result'
import { FreemanShiftDirtyMap, ShiftDirtyMap } from 'pages/shift/show/type'
import { addSnackbar } from 'store/snackbarSlice'
import { SecurityField } from 'types/securityField'
import { ApiShift, ShiftContent } from 'types/shift'
import { ShiftNote } from 'types/shiftNote'
import { ApiShiftQuestionnaires } from 'types/shiftQuestionnaire'
import { Icons } from 'variables'

const apiCall = {
  getSecurityFields: async () => {
    const res = await securityFieldApi().filter()
    return res.data
  },
  getQuestionnaires: async (
    securityFieldId: string,
    year: number,
    month: number,
  ) => {
    const res = await shiftQuestionnaireApi().filterBySecurityFieldAndYearMonth(
      securityFieldId,
      year,
      month,
    )
    return res.data
  },
  getShifts: async (securityFieldId: string, year: number, month: number) => {
    const res = await shiftApi().filterBySecurityField(
      securityFieldId,
      year,
      month,
    )
    return res.data
  },
  getShiftNotes: async (
    securityFieldId: string,
    year: number,
    month: number,
  ) => {
    const res = await securityFieldApi().filterShiftNoteByYearMonth(
      securityFieldId,
      year,
      month,
    )
    return res.data
  },
  getAutoAssign: async (
    securityFieldId: string,
    year: number,
    month: number,
  ) => {
    const res = await shiftApi().autoAssign(securityFieldId, year, month)
    return res.data
  },
  addShiftNote: async (
    securityFieldId: string,
    data: {
      securityId: string
      date: Dayjs
      note: string
    },
  ) => {
    await securityFieldApi().addShiftNote(securityFieldId, data)
  },
  updateShifts: async (
    securityFieldId: string,
    year: number,
    month: number,
    securityIds: string[],
    shifts: ShiftContent[],
  ) => {
    await shiftApi().bulkUpsert({
      securityFieldId,
      year,
      month,
      securityIds,
      shifts,
    })
  },
  getCSV: async (securityField: SecurityField, year: number, month: number) => {
    const res = await shiftApi().downloadCsv(securityField, year, month)
    return res
  },
  sendShift: async (securityFieldId: string, year: number, month: number) => {
    await securityFieldApi().shiftMail(securityFieldId, year, month)
  },
  updateShiftNote: async (shiftNote: ShiftNote) => {
    await securityFieldApi().updateShiftNote({ shiftNote })
  },
  deleteShiftNote: async (securityFieldId: string, id: string) => {
    await securityFieldApi().deleteShiftNote(securityFieldId, id)
  },
}

const Tab = {
  RESULT: 0,
  EDIT: 1,
} as const
type TabKey = (typeof Tab)[keyof typeof Tab]

const getDirtyMap = (
  dirtyMap: ShiftDirtyMap,
  securityId: string,
  dateIndex: number,
): {
  add: Set<string>
  remove: Set<string>
} => {
  const dirtyMapPerSecurity = dirtyMap.get(securityId) ?? []
  return (
    dirtyMapPerSecurity[dateIndex] ?? {
      add: new Set(),
      remove: new Set(),
    }
  )
}

const addDirtyMap =
  (dirtyMap: { add: Set<string>; remove: Set<string> }) =>
  (addValue: string): void => {
    if (dirtyMap.remove.has(addValue)) {
      dirtyMap.remove.delete(addValue)
    } else {
      dirtyMap.add.add(addValue)
    }
  }

const removeDirtyMap =
  (dirtyMap: { add: Set<string>; remove: Set<string> }) =>
  (removeValue: string): void => {
    if (dirtyMap.add.has(removeValue)) {
      dirtyMap.add.delete(removeValue)
    } else {
      dirtyMap.remove.add(removeValue)
    }
  }

const freemanDirtyMapOp = {
  get: (
    dirtyMap: FreemanShiftDirtyMap,
    lineIndex: number,
    dateIndex: number,
  ): {
    add: Set<string>
    remove: Set<string>
  } =>
    dirtyMap[lineIndex]?.[dateIndex] ?? {
      add: new Set(),
      remove: new Set(),
    },

  addSecurity: (
    dirtyMap: FreemanShiftDirtyMap,
    lineIndex: number,
    dateIndex: number,
    value: string,
  ) => {
    dirtyMap[lineIndex] = dirtyMap[lineIndex] ?? []
    dirtyMap[lineIndex][dateIndex] = {
      securityId: value,
      add: new Set(),
      remove: new Set(),
    }
  },

  removeSecurity: (
    dirtyMap: FreemanShiftDirtyMap,
    lineIndex: number,
    dateIndex: number,
  ) => {
    dirtyMap[lineIndex] = dirtyMap[lineIndex] ?? []
    dirtyMap[lineIndex][dateIndex] = null
  },

  addShift: (
    securityId: string,
    dirtyMap: FreemanShiftDirtyMap,
    lineIndex: number,
    dateIndex: number,
    addValue: string,
  ) => {
    dirtyMap[lineIndex] = dirtyMap[lineIndex] ?? []
    const diff = freemanDirtyMapOp.get(dirtyMap, lineIndex, dateIndex)
    if (diff.remove.has(addValue)) {
      diff.remove.delete(addValue)
    } else {
      diff.add.add(addValue)
    }

    dirtyMap[lineIndex][dateIndex] = {
      securityId: securityId,
      add: diff.add,
      remove: diff.remove,
    }
  },

  removeShift: (
    securityId: string,
    dirtyMap: FreemanShiftDirtyMap,
    lineIndex: number,
    dateIndex: number,
    removeValue: string,
  ) => {
    dirtyMap[lineIndex] = dirtyMap[lineIndex] ?? []
    const diff = freemanDirtyMapOp.get(dirtyMap, lineIndex, dateIndex)
    if (diff.add.has(removeValue)) {
      diff.add.delete(removeValue)
    } else {
      diff.remove.add(removeValue)
    }

    dirtyMap[lineIndex][dateIndex] = {
      securityId: securityId,
      add: diff.add,
      remove: diff.remove,
    }
  },
}

export default function ShiftShowPage() {
  const dispatch = useDispatch()
  const [selectDate, setSelectDate] = useState<Dayjs>(helper.day.today())
  const [securityField, setSecurityField] = useState<SecurityField | null>(null)
  const [shiftNotes, setShiftNotes] = useState<ShiftNote[]>([])
  const [isNotesModalOpen, setIsNotesModalOpen] = useState<ShiftNote | boolean>(
    false,
  )
  const { securityFieldId } = useParams<{ securityFieldId: string }>()
  if (securityFieldId === undefined) {
    // 404にリダイレクト
    return <Navigate to="/404" />
  }

  const [apiResponseQuestionnaire, setApiResponseQuestionnaire] = useState<{
    dates: Dayjs[]
    res: ApiShiftQuestionnaires
  } | null>(null)
  const [apiResponseShift, setApiResponseShift] = useState<{
    dates: Dayjs[]
    res: ApiShift
  } | null>(null)
  const [apiResponseAutoAssign, setApiResponseAutoAssign] =
    useState<ApiShift | null>(null)
  const [tab, setTab] = useState<TabKey>(Tab.RESULT)
  const [dirtyMap, setDirtyMap] = useState<ShiftDirtyMap>(new Map())
  const [freemanDirtyMap, setFreemanDirtyMap] = useState<FreemanShiftDirtyMap>(
    [],
  )

  const fetchSecurityField = async () => {
    const response = await apiCall.getSecurityFields()
    const securityField = response.find(
      (securityField) => securityField.id === securityFieldId,
    )
    setSecurityField(securityField ?? null)
  }

  const fetchShiftNotes = async () => {
    const response = await apiCall.getShiftNotes(
      securityFieldId,
      selectDate.year(),
      selectDate.month() + 1,
    )
    setShiftNotes(response)
  }

  const fetchResult = async (date: Dayjs) => {
    const response = await apiCall.getQuestionnaires(
      securityFieldId,
      date.year(),
      date.month() + 1,
    )
    setApiResponseQuestionnaire({
      dates: helper.day.getDays(date.year(), date.month() + 1),
      res: response,
    })
  }

  const fetchShift = async (date: Dayjs) => {
    const response = await apiCall.getShifts(
      securityFieldId,
      date.year(),
      date.month() + 1,
    )
    setApiResponseShift({
      dates: helper.day.getDays(date.year(), date.month() + 1),
      res: response,
    })
  }

  const fetchTabData = async (date: Dayjs) => {
    await fetchResult(date)
    await fetchShift(date)
  }

  const shiftNotesWithName = useMemo(() => {
    if (apiResponseShift == null) return []
    const securities = apiResponseShift.res.freemen.concat(
      apiResponseShift.res.securities,
    )

    // securityId -> name
    const securityMap = new Map<string, string>(
      securities.map((freeman) => [
        freeman.id,
        helper.formatter.nameFormat(freeman),
      ]),
    )

    return shiftNotes.map((note) => {
      return {
        ...note,
        securityName: securityMap.get(note.securityId) ?? '',
      }
    })
  }, [shiftNotes, apiResponseShift])

  const hasDirty = () => {
    // 固定隊員
    const fixedmanIsDirty = Array.from(dirtyMap.values())
      .map((v) => {
        return v.some((x) => x.add.size > 0 || x.remove.size > 0)
      })
      .some(Boolean)

    // 機動隊
    const freemanIsDirty = freemanDirtyMap.length > 0
    return fixedmanIsDirty || freemanIsDirty
  }

  // ページ初回表示時のデータ取得
  useEffect(() => {
    const clearAll = async () => {
      clearDirtyMap()
      setApiResponseAutoAssign(null)
      await fetchSecurityField()
      await fetchShiftNotes()
      await fetchTabData(selectDate)
    }

    clearAll().then()
  }, [selectDate])

  // 自動配置の反映
  useEffect(() => {
    // NOTE: 一旦、自動配置を行った際に変更差分を吹き飛ばす
    //       今後、変更差分を上書きしないように追加実装を行なってもいいのかも

    const dates = helper.day.getDays(selectDate.year(), selectDate.month() + 1)

    // === 固定隊員 ===
    const newDirtyMap: ShiftDirtyMap = new Map()
    apiResponseAutoAssign?.securities.forEach((security) => {
      const adds = dates.map(() => new Set<string>())
      security.shifts.forEach((shift) => {
        adds[helper.day.dateIndex(shift.date)].add(shift.workPatternId)
      })
      newDirtyMap.set(
        security.id,
        adds.map((add) => ({ add, remove: new Set() })),
      )
    })

    setDirtyMap(newDirtyMap)

    // === 機動隊 ===
    const minLength = new Set(
      apiResponseShift?.res.freemen
        .filter((freeman) => freeman.shifts.length > 0)
        .map((freeman) => freeman.id) ?? [],
    ).size

    const newFreemanDirtyMap: FreemanShiftDirtyMap = []
    apiResponseAutoAssign?.freemen.forEach((freeman) => {
      const adds = dates.map(() => ({
        securityId: freeman.id,
        add: new Set<string>(),
        remove: new Set<string>(),
      }))
      freeman.shifts.forEach((shift) => {
        const dateIndex = helper.day.dateIndex(shift.date)
        adds[dateIndex].add.add(shift.workPatternId)
      })

      // シフトが存在しているデータだけ格納
      const filtered = adds.map((addData) => {
        // データがあるセルだけ存続
        return addData.add.size > 0 ? addData : null
      })

      if (freeman.shifts.length > 0) {
        // データがある行だけ存続
        const dirtyMapLen = Math.max(minLength, newFreemanDirtyMap.length)
        newFreemanDirtyMap[dirtyMapLen] = filtered
      }
    })
    setFreemanDirtyMap(newFreemanDirtyMap)
  }, [apiResponseAutoAssign])

  const isCurrent = (tabTarget: TabKey): boolean => {
    return tab === tabTarget
  }

  const clearDirtyMap = () => {
    setDirtyMap(new Map())
    setFreemanDirtyMap([])
  }

  const handleSelectDate = (date: Dayjs | null) => {
    if (date == null) return
    if (!date.isValid()) return

    if (hasDirty()) {
      // TODO 移動をキャンセルした時、見かけ上の日付だけ移動してしまう
      //      日付セレクターにvalueとしてselectDateを渡しているが、
      //      表示の際、常にセレクターに渡しているvalueが参照されるわけではなさそう
      if (!window.confirm('変更が保存されていません。移動しますか？')) {
        return
      }
    }

    setSelectDate(date)
  }

  const handleChangeTab = (tab: TabKey) => () => {
    setTab(tab)
  }

  const handleShiftNoteAdd = (data: {
    securityId: string
    date: Dayjs
    note: string
  }) => {
    const apiAction = async () => {
      await apiCall.addShiftNote(securityFieldId, {
        securityId: data.securityId,
        date: data.date.add(data.date.utcOffset(), 'minutes'),
        note: data.note,
      })
      dispatch(
        addSnackbar({
          message: '備考を保存しました',
          severity: 'success',
        }),
      )
      await fetchShiftNotes()
      setIsNotesModalOpen(false)
    }

    apiAction().then()
  }

  const isShiftNoteOpened = () => {
    return isNotesModalOpen !== false
  }

  const shiftNoteDefaultValue = () => {
    if (typeof isNotesModalOpen === 'object') {
      return isNotesModalOpen
    }
  }

  const handleShiftNoteUpdate = (data: { shiftNote: ShiftNote }) => {
    const apiAction = async () => {
      await apiCall.updateShiftNote(data.shiftNote)
      dispatch(
        addSnackbar({
          message: '備考を更新しました',
          severity: 'success',
        }),
      )
      await fetchShiftNotes()
      setIsNotesModalOpen(false)
    }

    apiAction().then()
  }

  const handleShiftNoteDelete = (shiftId: string) => () => {
    if (!window.confirm('備考を削除しますか？')) return

    const apiAction = async () => {
      await apiCall.deleteShiftNote(securityFieldId, shiftId)
      dispatch(
        addSnackbar({
          message: '備考を削除しました',
          severity: 'success',
        }),
      )
      await fetchShiftNotes()
    }

    apiAction().then()
  }

  const handleShiftSaved = (securityIds: string[], shifts: ShiftContent[]) => {
    const apiAction = async () => {
      await apiCall.updateShifts(
        securityFieldId,
        selectDate.year(),
        selectDate.month() + 1,
        securityIds,
        shifts,
      )

      dispatch(
        addSnackbar({
          message: 'シフトを保存しました',
          severity: 'success',
        }),
      )

      await fetchShift(selectDate)
      clearDirtyMap()
    }

    apiAction().then()
  }

  const handleSendShift = () => {
    apiCall
      .sendShift(securityFieldId, selectDate.year(), selectDate.month() + 1)
      .then()
  }

  const handleDownloadCsv = () => {
    if (securityField == null) return
    apiCall
      .getCSV(securityField, selectDate.year(), selectDate.month() + 1)
      .then()
  }

  const handleAutoAssign = () => {
    if (
      !hasDirty() ||
      window.confirm('編集中のシフトは上書きされます\nよろしいですか？')
    ) {
      apiCall
        .getAutoAssign(
          securityFieldId,
          selectDate.year(),
          selectDate.month() + 1,
        )
        .then((data) => {
          setApiResponseAutoAssign(data)
          setTab(Tab.EDIT)
        })
    }
  }

  const handleUpdateDirtyMap = (
    securityId: string,
    date: Dayjs,
    value: {
      add?: string
      remove?: string
    },
  ) => {
    setDirtyMap((prev) => {
      const dateIndex = helper.day.dateIndex(date)
      const oldValue = getDirtyMap(prev, securityId, dateIndex)
      if (value.add) addDirtyMap(oldValue)(value.add)
      if (value.remove) removeDirtyMap(oldValue)(value.remove)

      const newMap = new Map(prev)
      const dirtyMapPerSecurity = newMap.get(securityId) ?? []
      dirtyMapPerSecurity[dateIndex] = oldValue
      newMap.set(securityId, dirtyMapPerSecurity)
      return newMap
    })
  }

  const handleUpdateFreemanDirtyMap = (
    originShiftIds: string[],
    lineIndex: number,
    dateIndex: number,
    securityId: string | null,
  ) => {
    setFreemanDirtyMap((prev) => {
      const newMap = [...prev]
      if (securityId) {
        freemanDirtyMapOp.addSecurity(newMap, lineIndex, dateIndex, securityId)

        // 元々入っていたシフトを削除
        originShiftIds.forEach((shiftId) => {
          freemanDirtyMapOp.removeShift(
            securityId,
            newMap,
            lineIndex,
            dateIndex,
            shiftId,
          )
        })
      } else {
        freemanDirtyMapOp.removeSecurity(newMap, lineIndex, dateIndex)
      }
      return newMap
    })
  }

  const handleUpdateFreemanShiftDirtyMap = (
    securityId: string,
    lineIndex: number,
    dateIndex: number,
    value: {
      // workPatternIds
      add?: string
      remove?: string
    },
  ) => {
    setFreemanDirtyMap((prev) => {
      const newMap = [...prev]
      if (value.add) {
        freemanDirtyMapOp.addShift(
          securityId,
          newMap,
          lineIndex,
          dateIndex,
          value.add,
        )
      }
      if (value.remove) {
        freemanDirtyMapOp.removeShift(
          securityId,
          newMap,
          lineIndex,
          dateIndex,
          value.remove,
        )
      }
      return newMap
    })
  }

  const handleClearDirtyMap = () => {
    clearDirtyMap()
  }

  const handleAddFreemanLine = () => {
    // 1件でもシフト登録されている隊員の数を取得
    const minLength = new Set(
      apiResponseShift?.res.freemen
        .filter((freeman) => freeman.shifts.length > 0)
        .map((freeman) => freeman.id) ?? [],
    ).size

    setFreemanDirtyMap((prev) => {
      const newMap = [...prev]
      newMap[Math.max(prev.length, minLength)] = []
      return newMap
    })
  }

  const handleOpenNotesModal = (data?: ShiftNote) => () => {
    if (data == null) {
      setIsNotesModalOpen(true)
    } else {
      setIsNotesModalOpen(data)
    }
  }

  const handleCloseNotesModal = () => {
    setIsNotesModalOpen(false)
  }

  return (
    <FlatLayout title="シフト" style={contentStyle}>
      <Box style={toolboxStyle}>
        <DatePickerForm
          name="date"
          value={selectDate}
          onChange={handleSelectDate}
          sx={monthSelectorStyle}
          format={helper.formatter.monthFormatStr}
          openTo="month"
          views={['month']}
          showArrowButton={true}
        />
        <Box style={tabButtonContainerStyle}>
          <Button
            style={tabButtonStyle}
            variant={isCurrent(Tab.RESULT) ? 'contained' : 'outlined'}
            onClick={handleChangeTab(Tab.RESULT)}
          >
            シフト確認
          </Button>
          <Button
            style={tabButtonStyle}
            variant={isCurrent(Tab.EDIT) ? 'contained' : 'outlined'}
            onClick={handleChangeTab(Tab.EDIT)}
          >
            シフト配置
          </Button>
        </Box>

        <Box style={subToolboxStyle}>
          <Button
            style={iconButtonStyle}
            variant={'outlined'}
            onClick={handleSendShift}
          >
            <Icons.sendShift style={iconStyle} />
            シフト送信
          </Button>
          <Button
            style={iconButtonStyle}
            variant={'outlined'}
            onClick={handleDownloadCsv}
          >
            <Icons.csvDownload style={iconStyle} />
            CSVダウンロード
          </Button>
          <Button
            style={iconButtonStyle}
            variant="contained"
            onClick={handleAutoAssign}
          >
            <Icons.autoAssign style={iconStyle} />
            シフト自動配置
          </Button>
        </Box>
      </Box>

      <Box style={informationStyle}>
        <Typography style={informationSecurityFieldStyle}>
          {securityField?.name ?? ''}
        </Typography>

        {isCurrent(Tab.EDIT) && (
          <Button variant="outlined" onClick={handleAddFreemanLine}>
            <Icons.addButton />
            追加
          </Button>
        )}

        {/*TODO 勤務パターン一覧*/}
      </Box>

      {apiResponseQuestionnaire && (
        <Box style={tabStyle(tab, Tab.RESULT)}>
          <ResultComponent
            questionnaires={apiResponseQuestionnaire.res}
            days={apiResponseQuestionnaire.dates}
          />
        </Box>
      )}

      {apiResponseShift && (
        <Box style={tabStyle(tab, Tab.EDIT)}>
          <EditComponent
            securityFieldId={securityFieldId}
            dirtyMap={dirtyMap}
            freemanDirtyMap={freemanDirtyMap}
            updateDirtyMap={handleUpdateDirtyMap}
            updateFreemanDirtyMap={handleUpdateFreemanDirtyMap}
            updateFreemanShiftDirtyMap={handleUpdateFreemanShiftDirtyMap}
            clearDirtyMap={handleClearDirtyMap}
            shift={apiResponseShift.res}
            days={apiResponseShift.dates}
            onSave={handleShiftSaved}
          />
        </Box>
      )}

      <Box>
        {shiftNotesWithName.map((note) => (
          <Box key={note.id} sx={noteStyle}>
            <Box style={noteTitleContainerStyle}>
              <Typography color="text.secondary" style={noteTitleStyle}>
                {note.securityName} {note.date.format('MM/DD (dd)')}
              </Typography>
              <IconButton
                style={noteIconStyle}
                onClick={handleOpenNotesModal(note)}
              >
                <Icons.editButton />
              </IconButton>
              <IconButton
                style={noteIconStyle}
                onClick={handleShiftNoteDelete(note.id)}
              >
                <Icons.deleteButton />
              </IconButton>
            </Box>
            <Typography>{note.note}</Typography>
          </Box>
        ))}
      </Box>

      <Button
        variant="outlined"
        onClick={handleOpenNotesModal()}
        style={noteAddButtonStyle}
      >
        <Icons.addButton />
        備考を追加
      </Button>

      <Dialog open={isShiftNoteOpened()} maxWidth={'sm'} fullWidth>
        <ModalLayout title="備考">
          <NotesModal
            onClose={handleCloseNotesModal}
            onAdd={handleShiftNoteAdd}
            onUpdate={handleShiftNoteUpdate}
            value={shiftNoteDefaultValue()}
            securities={apiResponseShift?.res.securities ?? []}
          />
        </ModalLayout>
      </Dialog>
    </FlatLayout>
  )
}

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 0,
  gap: '16px',
  overflowY: 'scroll',
}

const toolboxStyle: CSSProperties = {
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  gap: '8px',
}

const subToolboxStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  gap: '8px',
}

const informationStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '25px',
}

const informationSecurityFieldStyle: CSSProperties = {
  fontSize: '16px',
}

const iconButtonStyle: CSSProperties = {
  padding: '4px 8px',
}

const iconStyle: CSSProperties = {
  marginRight: '4px',
}

const tabButtonContainerStyle: CSSProperties = {
  borderRadius: '4px',
  overflow: 'hidden',
  border: '1px solid #ccc',
}

const tabButtonStyle: CSSProperties = {
  border: 'none',
  boxShadow: 'none',
  borderRadius: 0,
  height: '40px',
}

const tabStyle = (currentTab: TabKey, targetTab: TabKey): CSSProperties => {
  if (currentTab !== targetTab) {
    return {
      display: 'none',
    }
  } else {
    return {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minHeight: 'fit-content',
      maxHeight: '70%',
    }
  }
}

const monthSelectorStyle: CSSProperties = {
  width: '130px',
}

const noteTitleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '4px',
}

const noteTitleStyle: CSSProperties = {
  fontWeight: 'bold',
  marginBottom: '4px',
}

const noteStyle: CSSProperties = {
  marginBottom: '8px',
  borderBottom: '1px solid #BDBDBD',
}

const noteIconStyle: CSSProperties = {
  padding: '0',
}

const noteAddButtonStyle: CSSProperties = {
  width: 'fit-content',
  marginBottom: '16px',
}
